import { FC, useEffect, useMemo, useRef, useState } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import ptBR from "date-fns/locale/pt-BR";

import {
  FormGroup,
  Popper,
  TextField,
  Typography,
  useTheme,
  ClickAwayListener,
} from "@mui/material";

import { dateRangeFormat } from "../../utils/dateFormatter";

import { daysBR, monthsBR } from "../../shared/calendar";

import { DEFAULT_RANGE, sameRange, sanitizeRange } from "./helpers";
import { StyledBox } from "./style";

registerLocale("pt-BR", ptBR);

const ptbr: Locale = {
  localize: {
    ordinalNumber: (...args: any[]) => "",
    era: (...args: any[]) => "",
    quarter: (...args: any[]) => "",
    day: (n: any) => daysBR[n],
    month: (n: any) => monthsBR[n],
    dayPeriod: (...args: any[]) => "",
  },
  formatLong: {
    date: () => "dd/mm/yyyy",
    time: (...args: any[]) => "",
    dateTime: (...args: any[]) => "",
  },
};

interface CalendarRangeProps {
  name: string;
  label: string;
  onChangeDateStartEnd?: (dateStart: Date | null, dateEnd: Date | null) => void;
  disabled?: boolean;
  isCleanFilter?: boolean;
  dateInitialCalendar?: Date[];
  errorAlternative?: boolean;
  minDate?: Date;
  maxDate?: Date;
}

const getStartOrEnd = (startDate: Date | null, endDate: Date | null) =>
  dateRangeFormat(startDate, endDate) ?? "";

const CalendarRangeTwoMonths: FC<CalendarRangeProps> = ({
  name,
  label,
  onChangeDateStartEnd,
  disabled,
  isCleanFilter,
  dateInitialCalendar,
  errorAlternative,
  minDate,
  maxDate,
}) => {
  const [dateRange, setDateRange] = useState<Array<Date | null>>(DEFAULT_RANGE);

  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();

  const handleChange = (dates: [Date | null, Date | null], event: any) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const [start, end] = dates;
    setDateRange([start, end]);

    if (start && end) {
      onChangeDateStartEnd?.(start, end);
      setIsOpen(false);
    } else if (!start && !end) {
      onChangeDateStartEnd?.(null, null);
    }
  };

  const toggleDatePicker = () => {
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    setDateRange(sanitizeRange(dateInitialCalendar));
    const nextRange = sanitizeRange(dateInitialCalendar);
    setDateRange((prev) =>
      sameRange(prev as [Date | null, Date | null], nextRange)
        ? prev
        : nextRange
    );
  }, [dateInitialCalendar]);

  useEffect(() => {
    if (isCleanFilter) {
      setDateRange([null, null]);
    }
  }, [isCleanFilter]);

  const hasStartDateWithoutEndDate =
    dateRange[0] !== null && dateRange[1] === null;

  type DateRange = Array<Date | null>;

  function determineDateValue(isCleanFilter: boolean, dateRange: DateRange) {
    if (isCleanFilter) {
      return [];
    }
    const startDate: Date = dateRange[0] !== null ? dateRange[0] : new Date();
    const endDate: Date | null = dateRange[1] !== null ? dateRange[1] : null;

    return getStartOrEnd(startDate, endDate);
  }

  const formattedValue = useMemo(() => {
    return determineDateValue(isCleanFilter ?? false, dateRange);
  }, [dateRange, isCleanFilter]);

  const helperTextHandler = () => {
    if (errorAlternative) return "Selecione o intervalo de datas";

    if (hasStartDateWithoutEndDate) {
      return "Selecione a data final para filtrar";
    }

    return "";
  };

  const styleSxTextField = {
    width: "100%",
    backgroundColor: "#fff",
    maxHeight: "40.3px",
    "& .Mui-disabled": {
      background: "#E8E9EB",
    },
    "& .datepicker": {
      border: "2px solid #58595B",
    },
    "& .MuiInputLabel-shrink": {
      fontWeight: 700,
      backgroundColor: "#fff",
      borderRadius: "4px",
      color: "#58595B",
      paddingRight: 6,
      fontFamily: "Open Sans",
      fontSize: "20px",
    },
    "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
      border: `2px solid ${theme.palette.primary.main}`,
    },
    "& .MuiOutlinedInput": {
      fontSize: "20px",
    },
    "& .MuiOutlinedInput-input": {
      fontSize: "14px",
      padding: "4px 8px",
      fontFamily: "Open Sans",
      color: "#58595B",
      height: "32px",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderRadius: "4px",
      opacity: "0.6",
    },
  };

  return (
    <ClickAwayListener onClickAway={() => isOpen && setIsOpen(false)}>
      <FormGroup ref={wrapperRef} sx={{ position: "relative" }}>
        <Typography
          sx={{
            color: "#58595B",
            fontFamily: "Open Sans",
            fontSize: "1.6rem",
            fontWeight: "700",
            paddingBottom: "4px",
          }}
        >
          {label}
        </Typography>
        <TextField
          disabled={disabled}
          name={name}
          error={hasStartDateWithoutEndDate || errorAlternative}
          helperText={helperTextHandler()}
          placeholder="DD/MM/AAAA até DD/MM/AAAA"
          value={formattedValue}
          onClick={toggleDatePicker}
          variant="outlined"
          autoComplete="off"
          InputProps={{
            readOnly: true,
          }}
          sx={styleSxTextField}
          fullWidth
        />
        {isOpen && (
          <Popper
            open={isOpen}
            anchorEl={wrapperRef.current}
            placement="bottom-start"
            style={{ zIndex: 1300 }}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
          >
            <StyledBox>
              <DatePicker
                selected={dateRange[0]}
                onChange={(date, event) => handleChange(date, event)}
                startDate={dateRange[0]}
                endDate={dateRange[1]}
                selectsRange
                monthsShown={1}
                locale={ptbr}
                minDate={minDate}
                maxDate={maxDate}
                inline
              />
            </StyledBox>
          </Popper>
        )}
      </FormGroup>
    </ClickAwayListener>
  );
};

export default CalendarRangeTwoMonths;
